import { TAB_CONFIG } from "@/edu-resources";
import {
  type DestinationAccess,
  MODULE_DESTINATION_IDS,
  MODULE_DESTINATIONS,
  type NavigationDestination,
  resolveDestinationState,
} from "@/navigation";
import { SETTINGS_NAV_LINKS } from "@/settings";

import { RESOURCES_TAB_KEYWORDS } from "../constants/resources-tab-keywords";
import type { OmniSearchEntry, OmniSearchGroup } from "../types/omni-search";

/**
 * Lists every page the current user can open, in the order search shows them when nothing is typed:
 * the side nav modules (with the Resources tabs after Resources), then the settings sections.
 *
 * Only destinations the user sees enabled appear; a hidden or disabled one is left out. The index is
 * pure: it reads no Medplum data and holds no patient information.
 *
 * @param access - Facts about the current user and practice that decide which destinations they can
 * open and where each one leads.
 * @returns The search entries, each with a unique id.
 */
export function buildSearchIndex(access: DestinationAccess): OmniSearchEntry[] {
  const moduleEntries = MODULE_DESTINATIONS.flatMap((destination) => {
    const entry = toSearchEntry(destination, "Modules", access);
    if (!entry) return [];
    if (destination.id !== MODULE_DESTINATION_IDS.resources) return [entry];
    return [entry, ...buildResourcesTabEntries(entry)];
  });
  const settingsEntries = SETTINGS_NAV_LINKS.flatMap(
    (destination) => toSearchEntry(destination, "Settings", access) ?? [],
  );

  return [...moduleEntries, ...settingsEntries];
}

/**
 * The search entry for one destination, or `undefined` when search leaves it out. A destination
 * the user sees disabled can't be opened, so search leaves it out too.
 */
function toSearchEntry(
  destination: NavigationDestination,
  group: OmniSearchGroup,
  access: DestinationAccess,
): OmniSearchEntry | undefined {
  if (destination.isSearchable === false) return undefined;
  if (resolveDestinationState(destination.gates, access) !== "visible") return undefined;

  return {
    id: destination.id,
    label: destination.label,
    Icon: destination.Icon,
    group,
    description: destination.section,
    keywords: destination.keywords ?? [],
    path: destination.buildPath(access),
  };
}

/**
 * One entry per Resources tab. The page reads the tab from `?tab=`, so each entry deep-links to its
 * tab.
 */
function buildResourcesTabEntries(resourcesEntry: OmniSearchEntry): OmniSearchEntry[] {
  return Object.values(TAB_CONFIG).map((tab) => ({
    id: `${resourcesEntry.id}.tab.${tab.value}`,
    label: tab.label,
    Icon: resourcesEntry.Icon,
    group: "Modules",
    description: resourcesEntry.label,
    keywords: RESOURCES_TAB_KEYWORDS[tab.value],
    path: `${resourcesEntry.path}?tab=${tab.value}`,
  }));
}
